import pino from 'pino';
import isBuild from './build';

const { LOG_LEVEL, LOG_PRETTY } = process.env;

// Determine the final log level
const finalLogLevel = LOG_LEVEL ?? 'info';

// Build logger configuration
const createLoggerConfig = () => {
  const baseConfig = {
    level: finalLogLevel,
    timestamp: pino.stdTimeFunctions.isoTime,
    redact: ['password', 'token', 'secret', 'authorization'],
  };

  if (LOG_PRETTY === 'true') {
    return {
      ...baseConfig,
      transport: {
        target: 'pino-pretty',
        options: {
          colorize: true,
          translateTime: 'SYS:standard',
          ignore: 'pid,hostname',
        },
      },
    };
  }

  return baseConfig;
};

// Create logger
export const logger = pino(createLoggerConfig());

const sanitizeError = (error: unknown): unknown => {
  if (!error || typeof error !== 'object') return error;
  const e = error as Record<string, unknown>;

  const sanitized: Record<string, unknown> = { ...e };

  if (sanitized.networkError && typeof sanitized.networkError === 'object') {
    const { result: _result, response: _response, ...rest } = sanitized.networkError as Record<string, unknown>;
    sanitized.networkError = rest;
  }

  if (Array.isArray(sanitized.graphQLErrors)) {
    sanitized.graphQLErrors = sanitized.graphQLErrors.map((gqlErr: unknown) => {
      if (!gqlErr || typeof gqlErr !== 'object') return gqlErr;
      const { extensions: _extensions, ...rest } = gqlErr as Record<string, unknown>;
      return rest;
    });
  }

  return sanitized;
};

export const reportBuildError = (message: string, error: unknown, context: Record<string, unknown> = {}) => {
  logger.debug({ error: sanitizeError(error), ...context }, message);
};

export const reportRuntimeError = (message: string, error: unknown, context: Record<string, unknown> = {}) => {
  logger.error({ error: sanitizeError(error), ...context }, message);
};

const reportError = (message: string, error: unknown, context: Record<string, unknown> = {}) => {
  if (isBuild()) {
    reportBuildError(message, error, context);
  } else {
    reportRuntimeError(message, error, context);
  }
};

export default reportError;

